import React from "react";
import PropTypes from "prop-types";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  OWNERS_ADVERSE,
  OWNERS_CREDIT
} from "../../../../utils/constants/appConstants";

const FundtapNotesCell = ({
  value,
  updateSalesData,
  commentData,
  setCommentData,
  rowId,
  sectionEnum,
  noteValue
}) => {
  const handleChange = (event) => {
    setCommentData((prev) => ({ ...prev, [rowId]: event.target.value }));
  };

  const handleBlur = (event) => {
    if (updateSalesData)
      updateSalesData(rowId, event.target.value, sectionEnum);
  };

  const displayValue = commentData?.[rowId] ?? noteValue ?? value ?? "";

  return (
    <textarea
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-100"
    />
  );
};

FundtapNotesCell.propTypes = {
  value: PropTypes.string,
  updateSalesData: PropTypes.func,
  commentData: PropTypes.object,
  setCommentData: PropTypes.func,
  rowId: PropTypes.any,
  sectionEnum: PropTypes.string,
  noteValue: PropTypes.string
};

// === Tooltip Header Cell Component ===
const TooltipHeaderCell = ({ cell }) => {
  const { hoverText, sectionEnum } = cell.row.original;
  const tooltipId = `tooltip-${sectionEnum}`;

  return (
    <>
      <div
        data-tooltip-id={tooltipId}
        data-tooltip-content={hoverText}
        className={hoverText ? "tooltip-enabled" : ""}
      >
        {cell.value}
      </div>
      {hoverText && (
        <ReactTooltip
          id={tooltipId}
          className="tooltipcustom tooltip-arrow-left"
          place="bottom-start"
          delayShow={300}
        />
      )}
    </>
  );
};

TooltipHeaderCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string,
    row: PropTypes.shape({
      original: PropTypes.shape({
        hoverText: PropTypes.string,
        sectionEnum: PropTypes.string
      })
    })
  })
};

const OwnerCell = ({ cell }) => {
  const { owners } = cell.row.original;
  const ownerKey = cell.column.id;
  const status = cell.row.original[`${ownerKey}_color`];

  if (owners === "Property") {
    let statusClass = "";
    if (status === "No") statusClass = "owner-property-cell--no";
    else if (status === "Yes") statusClass = "owner-property-cell--yes";
    else if (status === "Yes - CONFIRMED")
      statusClass = "owner-property-cell--confirmed";

    return (
      <div className={`owner-property-cell ${statusClass}`}>{cell.value}</div>
    );
  }

  // Handle creditScore and adverse coloring
  if (owners === OWNERS_CREDIT || owners === OWNERS_ADVERSE) {
    const color = status;
    return (
      <div
        className="owner-property-cell"
        style={{ backgroundColor: color || "inherit" }}
      >
        {cell.value}
      </div>
    );
  }

  return cell.value;
};

OwnerCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    row: PropTypes.shape({
      original: PropTypes.object
    }),
    column: PropTypes.shape({
      id: PropTypes.string
    })
  })
};

export const BUSINESS_COLUMNS = [
  { Header: "BUSINESS", Footer: "BUSINESS", accessor: "business" },
  { Header: "NUMBERS", Footer: "NUMBERS", accessor: "numbers" },
  { Header: "DETAILS", Footer: "DETAILS", accessor: "details" },
  { Header: "", Footer: "", accessor: "blank" },
  {
    Header: "FUNDTAP NOTES",
    Footer: "FUNDTAP NOTES",
    accessor: "fundtap_notes",
    Cell: FundtapNotesCell
  }
];

export const TRUSTS_COLUMNS = [
  {
    Header: "TRUSTS & CORPORATES",
    Footer: "TRUSTS & CORPORATES",
    accessor: "trusts_corporates"
  },
  {
    Header: "TRUSTEE / DIRECTOR 1",
    Footer: "TRUSTEE / DIRECTOR 1",
    accessor: "director_1"
  },
  {
    Header: "TRUSTEE / DIRECTOR 2",
    Footer: "TRUSTEE / DIRECTOR 2",
    accessor: "director_2"
  },
  {
    Header: "TRUSTEE / DIRECTOR 3",
    Footer: "TRUSTEE / DIRECTOR 3",
    accessor: "director_3"
  },
  {
    Header: "FUNDTAP NOTES",
    Footer: "FUNDTAP NOTES",
    accessor: "fundtap_notes",
    Cell: FundtapNotesCell
  }
];

export const FINANCIALS_COLUMNS = [
  { Header: "FINANCIALS", Footer: "FINANCIALS", accessor: "financials" },
  { Header: "NOW -12M", Footer: "NOW -12M", accessor: "now_12m" },
  { Header: "12-24M", Footer: "12-24M", accessor: "m12_m24" },
  { Header: "24-36M", Footer: "24-36M", accessor: "m24_m36" },
  {
    Header: "FUNDTAP NOTES",
    Footer: "FUNDTAP NOTES",
    accessor: "fundtap_notes",
    Cell: FundtapNotesCell
  }
];

export function generateOwnersTableData(salesOwnerProperty) {
  if (!Array.isArray(salesOwnerProperty) || salesOwnerProperty.length === 0)
    return { data: [], columns: [] };

  const sortedOwners = salesOwnerProperty
    .slice()
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const rows = [
    { label: "Name", accessor: "fullName", enum: "NAME" },
    {
      label: "Credit Score",
      accessor: "creditScore",
      accessorColor: "creditScoreColor",
      isProperty: true,
      enum: "CREDIT_SCORE",
      hoverText: "Green > 600, Orange > 500, Red <= 500"
    },
    {
      label: "Adverse",
      accessor: "adverse",
      accessorColor: "adverseColor",
      isProperty: true,
      enum: "ADVERSE",
      hoverText: "Red if any adverse. Green if Not"
    },
    {
      label: "Property",
      accessor: "propertyStatus",
      accessorColor: "propertyStatus",
      isProperty: true,
      enum: "PROPERTY",
      hoverText: "Green if Yes - CONFIRMED, Yellow if Yes, Otherwise Red"
    },
    {
      label: "Property Ownership",
      accessor: "propertyOwnership",
      enum: "PROPERTY_OWNERSHIP"
    },
    { label: "Address", accessor: "propertyAddress", enum: "ADDRESS" },
    {
      label: "property value",
      accessor: "value",
      isCurrency: true,
      enum: "PROPERTY_VALUE"
    },
    {
      label: "property equity",
      accessor: "equity",
      isCurrency: true,
      enum: "PROPERTY_EQUITY"
    },
    {
      label: "Personal PPSR",
      accessor: "personalPpsr",
      enum: "PERSONAL_PPSR"
    }
  ];

  const data = rows.map((row) => {
    const rowData = {
      owners: row.label,
      sectionEnum: row.enum,
      hoverText: row.hoverText || ""
    };
    sortedOwners.forEach((owner, idx) => {
      let cellValue = owner[row.accessor];
      if (row.accessor === "propertyOwnership") {
        if (!cellValue || cellValue === "NA") {
          cellValue = "Private";
        }
      }
      if (row.isCurrency && cellValue != null && cellValue !== "") {
        const num = Number(cellValue);
        cellValue = isNaN(num) ? cellValue : `$${num.toLocaleString()}`;
      }
      rowData[`owner_${idx + 1}`] = cellValue != null ? cellValue : "";
      if (row.isProperty) {
        rowData[`owner_${idx + 1}_color`] = owner[row.accessorColor];
      }
    });
    return rowData;
  });

  const columns = [
    {
      Header: "OWNERS",
      accessor: "owners",
      Cell: TooltipHeaderCell
    },
    ...sortedOwners.map((owner, idx) => ({
      Header: `OWNER ${idx + 1}`,
      accessor: `owner_${idx + 1}`,
      id: `owner_${idx + 1}`,
      Cell: OwnerCell
    })),
    {
      Header: "FUNDTAP NOTES",
      Footer: "FUNDTAP NOTES",
      accessor: "fundtap_notes",
      Cell: FundtapNotesCell
    }
  ];

  return { data, columns };
}
